// =====================================================================
// PaymentDialog (Financeiro F1 / S58) — registra a baixa de uma conta a receber
// e oferece o recibo (PDF). Padrão MUI v4.
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  CircularProgress
} from "@material-ui/core";
import { toast } from "react-toastify";
import {
  payReceivable,
  ensureReceipt,
  openReceiptPdf,
  uploadPaymentReceipt,
  openPaymentReceipt,
  fmtMoney,
  PAYMENT_METHODS,
  CARD_BRANDS,
  financeErrorMessage
} from "../../services/financeService";
import useIsMobile from "../../utils/useIsMobile";

const PaymentDialog = ({ receivable, open, onClose, onPaid, accounts = [] }) => {
  const isMobile = useIsMobile();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("dinheiro");
  const [guide, setGuide] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(null);
  const [accountId, setAccountId] = useState("");
  const [cardBrand, setCardBrand] = useState("visa");
  const [cardInstallments, setCardInstallments] = useState("1");
  const [payerName, setPayerName] = useState(""); // [S96] quem pagou (!= paciente)
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [hasReceipt, setHasReceipt] = useState(false);

  useEffect(() => {
    if (open && receivable) {
      const rem = receivable.remaining != null ? receivable.remaining : receivable.amount;
      setAmount(rem != null ? String(rem) : "");
      setMethod("dinheiro");
      setGuide("");
      setNotes("");
      setPaid(null);
      setAccountId(String((accounts.find((a) => a.isDefault) || accounts[0] || {}).id || ""));
      setCardBrand("visa");
      setCardInstallments("1");
      setPayerName("");
      setReceiptBusy(false);
      setHasReceipt(false);
    }
  }, [open, receivable, accounts]);

  // [S91] anexa a foto/PDF do comprovante da maquininha ao pagamento já registrado.
  const handleAttachReceipt = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // permite reanexar o mesmo arquivo
    if (!file || !paid) return;
    setReceiptBusy(true);
    try {
      await uploadPaymentReceipt(paid.id, file);
      setHasReceipt(true);
      toast.success("Comprovante anexado. 📎");
    } catch (err) {
      toast.error(financeErrorMessage(err));
    } finally {
      setReceiptBusy(false);
    }
  };

  const handleViewReceipt = async () => {
    try {
      await openPaymentReceipt(paid.id);
    } catch (err) {
      toast.error(financeErrorMessage(err));
    }
  };

  const handlePay = async () => {
    const value = Number(String(amount).replace(",", "."));
    if (!value || value <= 0) {
      toast.error("Informe um valor válido.");
      return;
    }
    setLoading(true);
    try {
      const payload = { amount: value, method, notes };
      if (payerName) payload.payerName = payerName; // [S96] quem pagou (!= paciente)
      if (method === "convenio" && guide) payload.insuranceGuide = guide;
      if (accountId) payload.accountId = Number(accountId);
      if (method === "debito" || method === "credito") {
        payload.cardBrand = cardBrand;
        if (method === "credito") payload.cardInstallments = Number(cardInstallments) || 1;
      }
      const payment = await payReceivable(receivable.id, payload);
      setPaid(payment);
    } catch (err) {
      toast.error(financeErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleReceipt = async () => {
    try {
      const receipt = await ensureReceipt(paid.id);
      await openReceiptPdf(receipt.id);
    } catch (err) {
      toast.error(financeErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>{paid ? "Pagamento registrado" : "Registrar pagamento"}</DialogTitle>
      <DialogContent>
        {receivable && (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {receivable.patient?.fullName || "Paciente"} — {receivable.description || "Conta"}
            <br />
            Saldo: <strong>{fmtMoney(receivable.remaining)}</strong>
          </Typography>
        )}

        {!paid ? (
          <>
            <TextField
              label="Valor recebido"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              margin="dense"
              type="number"
              inputProps={{ step: "0.01", min: "0" }}
            />
            <TextField
              select
              label="Forma de pagamento"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              fullWidth
              margin="dense"
            >
              {PAYMENT_METHODS.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>
            {accounts.filter((a) => a.active).length > 0 && (
              <TextField
                select
                label="Conta (onde entra)"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                fullWidth
                margin="dense"
              >
                {accounts.filter((a) => a.active).map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>
                ))}
              </TextField>
            )}
            {(method === "debito" || method === "credito") && (
              <>
                <TextField select label="Bandeira" value={cardBrand} onChange={(e) => setCardBrand(e.target.value)} fullWidth margin="dense">
                  {CARD_BRANDS.filter((b) => b.value !== "todas").map((b) => (
                    <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                  ))}
                </TextField>
                {method === "credito" && (
                  <TextField label="Parcelas" type="number" value={cardInstallments}
                    onChange={(e) => setCardInstallments(e.target.value)} fullWidth margin="dense"
                    inputProps={{ min: 1, max: 24 }} helperText="Crédito parcelado gera 1 previsão por parcela." />
                )}
              </>
            )}
            {method === "convenio" && (
              <TextField
                label="Guia do convênio"
                value={guide}
                onChange={(e) => setGuide(e.target.value)}
                fullWidth
                margin="dense"
              />
            )}
            <TextField
              label="Observação (opcional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              margin="dense"
              multiline
            />
            <TextField
              label="Quem pagou? (opcional, se diferente do paciente)"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
              fullWidth
              margin="dense"
              placeholder="Ex.: cônjuge/familiar que passou o cartão"
            />
          </>
        ) : (
          <Box mt={1}>
            <Typography variant="body1" gutterBottom>
              Recebido {fmtMoney(paid.amount)} com sucesso. ✅
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Emita o recibo agora, se quiser.
            </Typography>
            <Box mt={2}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Comprovante da maquininha (opcional)
              </Typography>
              <Button
                component="label"
                variant="outlined"
                size="small"
                disabled={receiptBusy}
                startIcon={receiptBusy ? <CircularProgress size={16} /> : null}
              >
                {hasReceipt ? "Trocar comprovante" : "📎 Anexar comprovante"}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  hidden
                  onChange={handleAttachReceipt}
                />
              </Button>
              {hasReceipt && (
                <Button size="small" onClick={handleViewReceipt} style={{ marginLeft: 8 }}>
                  Ver
                </Button>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!paid ? (
          <>
            <Button onClick={onClose} disabled={loading}>Cancelar</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePay}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              Registrar
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleReceipt}>Abrir recibo</Button>
            <Button variant="contained" color="primary" onClick={() => onPaid && onPaid()}>
              Concluir
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
